import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import styles from './PeriodSelector.module.css';

import { Label, CheckboxButtonBar, FeedbackBox } from 'components';

export interface PeriodSelectorProps {
  /** Indicates how many periods are shown. */
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  maxPeriods: 6 | 7;
  /** array of period values [3, 5 ...] */
  defaultValues?: number[];
  label?: string;
  labelPosition?: 'top' | 'left';
  /** Shows red * */
  required?: boolean;
  /** error text to display if set */
  notValidatedText?: string;
  /** Returns array of selected values as number */
  onChange: (values: number[]) => void;
}

const SEMESTER_1 = 0;
const SEMESTER_2 = 1;
const SEMESTER_3 = 2;

const PERIODS_PER_SEMESTER = 3;
const MAX_PERIODS = 7;

const chunkPeriods = (array: number[]): number[][] => {
  const result: number[][] = [];
  for (let count = 0; count < array.length; count += PERIODS_PER_SEMESTER) {
    result.push(array.slice(count, count + PERIODS_PER_SEMESTER));
  }
  return result;
};

/** Checkbox group for selecting periods */
export const PeriodSelector = ({
  maxPeriods = MAX_PERIODS,
  defaultValues,
  label,
  labelPosition = 'top',
  required = false,
  notValidatedText = '',
  onChange,
}: PeriodSelectorProps) => {
  const maxPeriodsList = Array.from({ length: maxPeriods }, (_, index) => index + 1);
  const chunkedPeriods = chunkPeriods(maxPeriodsList);

  const checkboxItems = chunkedPeriods.map((periods, _) => {
    return periods.map((period) => ({
      label: period.toString(),
      value: period.toString(),
    }));
  });

  const returnValues = useRef<number[][]>(
    chunkedPeriods.map((semester) =>
      semester.reduce((periods, period) => {
        if (defaultValues?.includes(period)) periods.push(period);
        return periods;
      }, [] as number[])
    )
  );
  const [disableSemesters, setDisableSemesters] = useState<number[]>([]);

  const disableGroups = (selects: number[][]) => {
    if (selects[SEMESTER_1].length !== 0 || selects[SEMESTER_2].length !== 0) return [SEMESTER_3];
    if (selects[SEMESTER_3].length !== 0) return [SEMESTER_1, SEMESTER_2];
    return [];
  };

  const handleChange = (values: string[], index: number) => {
    returnValues.current[index] = values.map((num) => Number(num));
    setDisableSemesters(disableGroups(returnValues.current));
    onChange([...returnValues.current.flat()].sort());
  };

  useEffect(() => {
    setDisableSemesters(disableGroups(returnValues.current));
  }, []);

  return (
    <div
      className={clsx(
        styles['period-selector__container'],
        styles[`period-selector__container--label-${labelPosition}`]
      )}
    >
      {label && (
        <Label htmlFor="" required={required}>
          {label}
        </Label>
      )}
      <div className={styles['period-selector__selector-container']}>
        <div className={styles['period-selector__selector-row']}>
          {checkboxItems.map((checkboxes, semester) => (
            <CheckboxButtonBar
              key={`group_${semester}`}
              checkboxes={checkboxes}
              defaultValues={returnValues.current[semester].map((num) => num.toString())}
              disableGroup={disableSemesters.includes(semester)}
              required={required}
              onSetValues={(event) => handleChange(event, semester)}
              notValid={notValidatedText !== ''}
            />
          ))}
        </div>
        {notValidatedText !== '' && <FeedbackBox level="error" feedback={notValidatedText} />}
      </div>
    </div>
  );
};
